import { SearchInput } from 'components/form/Input';
import CustomTable from 'components/layouts/CustomTable';
import Loader from 'components/layouts/Loader';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useUserStore, { UserProps } from 'state/userStore';
import { z } from 'zod';
import { getAllTransactions, updateTransaction } from 'api/transaction';
import { NumberDisplay } from 'components/layouts/Display';
import clsx from 'clsx';
import CopyComponent from 'components/layouts/CopyComponent';
import { SelectWithoutControl } from 'components/form/SelectInput';

export enum TransactionStatus {
  PENDING = 'PENDING',
  DECLINED = 'DECLINED',
  CONFIRMED = 'CONFIRMED',
}

export enum TransactionType {
  DEPOSIT = 'DEPOSIT',
  WITHDRAWAL = 'WITHDRAWAL',
}

export const TransactionSchema = z.object({
  amount: z.number(),
  status: z.nativeEnum(TransactionStatus).default(TransactionStatus.PENDING),
  plan: z.string().optional(),
  crypto: z.string(),
  transactionId: z.string().optional(),
  trxType: z.nativeEnum(TransactionType),
  imageUrl: z.string().optional(),
  wallet: z.string().optional(),
});

export const UpdateTrxSchema = z.object({
  status: z.nativeEnum(TransactionStatus).default(TransactionStatus.PENDING),
});

export type TransactionModel = z.infer<typeof TransactionSchema>;

type TransactionData = { user: UserProps; sn: number } & {
  _id: string;
  amount: string;
  trxType: TransactionType;
  plan?: string;
  transactionId?: string;
  crypto: string;
  status: TransactionStatus;
  imageUrl?: string;
  wallet?: string;
};

const AllTransactions = () => {
  window.document.title = 'Transactions';

  const { user } = useUserStore();

  const [data, setData] = useState<TransactionData[]>([]);
  const [query, setQuery] = useState('');
  //   const [selectedUser, setSelectedUser] = useState<TransactionData | null>(
  //     null
  //   );
  //   const [isOpen, setIsOpen] = useState(false);
  const [searchList, setSearchList] = useState<TransactionData[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchTransactions = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await getAllTransactions(user.type);

      const users: TransactionData[] = res.data;

      setData(
        users.map((user, index) => ({
          ...user,
          sn: index + 1,
        }))
      );
    } catch (error: any) {
      const { errors } = error.response.data;

      errors.forEach((err: { error: string }) => {
        toast.error(error);
      });

      console.log(error);
    }
    setLoading(false);
  };

  const columns = [
    {
      Header: 'S/N',
      accessor: 'sn',
      Cell: ({ row: { original } }: any) => {
        const { sn } = original;
        return <p>{sn}</p>;
      },
    },
    {
      Header: 'Full Name',
      accessor: 'fullName',
      minWidth: '200px',
      Cell: ({ row: { original } }: any) => {
        const {
          user: { fullName },
        } = original;
        return <p>{fullName}</p>;
      },
    },
    {
      Header: 'Email',
      accessor: 'email',
      Cell: ({ row: { original } }: any) => {
        const {
          user: { email },
        } = original;
        return <p>{email}</p>;
      },
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      minWidth: '150px',
      Cell: ({ row: { original } }: any) => {
        const { amount } = original;
        return <NumberDisplay displayType="text" value={+amount} prefix="$" />;
      },
    },
    {
      Header: 'Transaction Type',
      accessor: 'trxType',
      minWidth: '200px',
      Cell: ({ row: { original } }: any) => {
        const { trxType } = original;
        return (
          <p
            className={clsx(
              trxType === TransactionType.DEPOSIT
                ? 'text-green-600'
                : 'text-amber-500'
            )}
          >
            {trxType}
          </p>
        );
      },
    },
    {
      Header: 'Crypto Currency',
      accessor: 'crypto',
      minWidth: '200px',
      Cell: ({ row: { original } }: any) => {
        const { crypto } = original;
        return <p className="capitalize">{crypto}</p>;
      },
    },
    {
      Header: 'Status',
      accessor: 'status',
      minWidth: '200px',
      Cell: ({ row: { original } }: any) => {
        const { status, _id } = original;
        return (
          <SelectWithoutControl
            defaultVal={status}
            name="status"
            options={['PENDING', 'CONFIRMED', 'DECLINED']}
            handleChange={(value: string) => handleUpdate(value, _id)}
          />
        );
      },
    },
    {
      Header: 'Plan',
      accessor: 'plan',
      minWidth: '150px',
      Cell: ({ row: { original } }: any) => {
        const { plan } = original;
        return <p className="capitalize">{plan ?? 'NaN'}</p>;
      },
    },
    {
      Header: 'Wallet',
      accessor: 'wallet',
      minWidth: '200px',
      Cell: ({ row: { original } }: any) => {
        const { wallet } = original;
        return wallet ? <CopyComponent text={wallet} /> : 'NaN';
      },
    },
    {
      Header: 'Image URL',
      accessor: 'imageUrl',
      minWidth: '150px',
      Cell: ({ row: { original } }: any) => {
        const { imageUrl } = original;
        return imageUrl ? (
          <a
            href={imageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full px-4 py-1 text-blue-500 hover:bg-blue-500 hover:text-white"
          >
            view
          </a>
        ) : (
          <p className="px-4 py-1">NaN</p>
        );
      },
    },
  ];

  const handleUpdate = async (status: string, userId: string) => {
    if (!user) return;
    try {
      const res = await updateTransaction({
        userType: user.type,
        data: { status },
        userId,
      });

      if (res?.data) {
        toast.success('transaction updated');
        const trx = data.map((item) => {
          if (item._id === res.data._id) {
            return {
              ...item,
              status: res.data.status,
            };
          }
          return item;
        });

        setData(trx);
      }
    } catch (error: any) {
      const { errors } = error.response.data;
      errors.map((err: { error: string }) => {
        return toast.error(err.error);
      });
      console.log(error);
    }
  };

  //   const handleChange = (value: string, userId) => {

  //   };

  const handleSearch = (searchStr: string) => {
    setQuery(searchStr);
    const studentList = data.filter(
      (item) =>
        item.user.fullName.toLowerCase().startsWith(searchStr) ||
        item.user.fullName.toLowerCase().endsWith(searchStr) ||
        item.user.email.toLowerCase().startsWith(searchStr)
    );

    setSearchList(studentList);
  };

  //   const selectUser = (data: TransactionData) => {
  //     setSelectedUser(data);
  //     setIsOpen(true);
  //   };

  //   const handleClose = () => {
  //     setSelectedUser(null);
  //     setIsOpen(false);
  //   };

  useEffect(() => {
    fetchTransactions();
  }, []);

  return (
    <div className="px-5">
      <div className="flex flex-col gap-y-4">
        <h3 className="text-xl uppercase">Transactions Table</h3>
        <SearchInput
          name="search"
          placeholder="search by full name"
          handleChange={(value) => handleSearch(value)}
          value={query}
        />
      </div>
      <br />
      {loading ? (
        <Loader />
      ) : (
        <CustomTable
          data={searchList.length ? searchList : data}
          columnShape={columns}
          size={20}
        />
      )}
    </div>
  );
};

export default AllTransactions;
